/**
 * Bilder vor dem Hochladen verkleinern.
 *
 * Handykameras liefern 4–8 MB je Aufnahme; für die Dokumentation eines
 * Kratzers genügt die lange Kante mit 1600 Pixeln. Das spart Platz auf dem
 * Datenträger und macht das Hochladen über Mobilfunk erträglich.
 *
 * Schlägt etwas fehl oder wäre das Ergebnis nicht kleiner, wird die
 * Originaldatei zurückgegeben — lieber ein großes Bild als gar keines.
 */
export async function verkleinern(datei: File): Promise<File> {
  // HEIC kann der Browser meist nicht zeichnen; unverändert durchreichen.
  if (!datei.type.startsWith('image/') || datei.type === 'image/heic') return datei;

  const bild = await createImageBitmap(datei).catch(() => null);
  if (!bild) return datei;

  const MAX = 1600;
  const faktor = Math.min(1, MAX / Math.max(bild.width, bild.height));
  if (faktor === 1 && datei.size < 1_500_000) return datei;

  const leinwand = document.createElement('canvas');
  leinwand.width = Math.round(bild.width * faktor);
  leinwand.height = Math.round(bild.height * faktor);

  const kontext = leinwand.getContext('2d');
  if (!kontext) return datei;
  kontext.drawImage(bild, 0, 0, leinwand.width, leinwand.height);

  const blob = await new Promise<Blob | null>((resolve) =>
    leinwand.toBlob(resolve, 'image/jpeg', 0.82),
  );
  if (!blob || blob.size >= datei.size) return datei;

  return new File([blob], datei.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
}
